import type { Timestamp } from "firebase/firestore";

export const NOTIFICATION_CATEGORIES = [
  "mentions",
  "calendar",
  "calendarComments",
  "loveNote",
  "lists",
  "listItems",
  "todos",
  "todoComments",
  "importantDates",
  "notes",
  "links",
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];
// Absent/undefined means "on" — an opt-out model, so existing users who
// never open the settings page still get notified once push is enabled.
export type NotificationPrefs = Partial<Record<NotificationCategory, boolean>>;

export interface UserProfile {
  uid: string;
  username: string;
  usernameLower: string;
  displayName: string;
  createdAt: Timestamp;
  fcmToken?: string | null;
  notificationPrefs?: NotificationPrefs;
}

export type InviteStatus = "pending" | "accepted" | "declined" | "cancelled";

export interface Invite {
  id: string;
  fromUid: string;
  fromUsername: string;
  fromDisplayName: string;
  toUid: string;
  toUsername: string;
  status: InviteStatus;
  createdAt: Timestamp;
}

export interface Couple {
  id: string;
  memberUids: string[];
  memberNames: Record<string, string>;
  // Unguessable secret for the read-only .ics calendar feed (see
  // functions/src/index.ts icsFeed) — set lazily on first "Kalender
  // abonnieren" tap, not at couple creation.
  icsToken?: string;
  // "YYYY-MM-DD" — optional, set in Account.tsx. Absent/null means the
  // "zusammen seit X Tagen" line under the names on Home just doesn't show,
  // same opt-in idea as showOnHome on ImportantDate. Client writes to this
  // one field are allowed by firestore.rules even though the couple doc is
  // otherwise immutable from the client (everything else needs a Cloud
  // Function) — see the scoped `affectedKeys().hasOnly([...])` update rule.
  togetherSince?: string | null;
  // Set by the leaveCouple Cloud Function when one partner deletes their
  // account — memberUids drops to just the remaining partner at that point,
  // but none of the shared content is touched, so this is purely a status
  // flag for the UI (see Account.tsx) to explain why "partner" is now null.
  partnerLeftUid?: string;
  partnerLeftAt?: Timestamp;
  createdAt: Timestamp;
}

// One doc per partner, doc id = their own uid — same shape and reasoning as
// Mood (couples/{coupleId}/moods/{uid}): only the owner can write their own,
// both partners can read either. Only ever written while location sharing is
// explicitly turned on (see useLocationSharing.ts); turning it off deletes
// the doc outright rather than leaving stale coordinates behind. The app
// only ever shows the computed distance in km between the two docs — it
// never surfaces lat/lng or a place name in the UI.
export interface CoupleLocation {
  uid: string;
  lat: number;
  lng: number;
  updatedAt: Timestamp;
}

export interface EventCategory {
  id: string;
  name: string;
  color: string; // hex, e.g. "#6e2d49"
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

export type EventRecurrence = "none" | "weekly" | "monthly" | "yearly";

export interface CalendarEvent {
  id: string;
  title: string;
  startDate: string; // "YYYY-MM-DD" — for a recurring event, the anchor/first occurrence
  endDate: string; // "YYYY-MM-DD" — same as startDate for single-day and all recurring events
  time: string | null; // "HH:MM", only meaningful for single-day events
  endTime?: string | null; // "HH:MM" — end of a von–bis range; absent/null means no range, just `time`
  notes: string;
  categoryId: string | null;
  recurrence: EventRecurrence;
  // Total number of occurrences (including the first, on startDate) for a
  // recurring event — absent/null means it repeats indefinitely, same as
  // before this field existed.
  recurrenceCount?: number | null;
  // Set when this event was created from (or later linked to) a Todo —
  // editing title/date on either side keeps both in sync, see Todos.tsx and
  // Calendar.tsx. Absent means a normal, standalone event.
  linkedTodoId?: string | null;
  createdByUid: string;
  createdByName: string;
  // Set only when an existing event is edited (never on creation), so the
  // Cloud Function notifying on a notes change can tell who to exclude —
  // falls back to createdByUid when absent (never edited since creation).
  lastEditedByUid?: string;
  lastEditedByName?: string;
  // Denormalized count of the calendarEvents/{id}/comments subcollection,
  // kept in sync by onCalendarCommentCreated — same pattern as Todo.commentCount.
  commentCount?: number;
  // Also set by onCalendarCommentCreated — same reasoning as Todo's fields.
  lastCommentAt?: Timestamp;
  lastCommentByUid?: string;
  lastCommentByName?: string;
  lastCommentText?: string;
  createdAt: Timestamp;
}

// A chronological discussion thread per event, for coordinating ("sich
// abstimmen") — e.g. agreeing on a time, confirming who's bringing what.
// Same shape and reasoning as TodoComment: either partner can post any
// number of times, unlike the single `notes` field above.
export interface CalendarEventComment {
  id: string;
  text: string;
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
  // Set when the author edits it after the fact — only they can (see
  // firestore.rules); absent means never edited.
  editedAt?: Timestamp;
}

export interface WirList {
  id: string;
  name: string;
  icon: string; // emoji
  // Gates whether the grocery-category chips show on this list (see
  // ListDetail.tsx) — absent on existing lists, which fall back to
  // inferring it there instead of needing a migration.
  type?: "shopping" | "general";
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

export interface ListItem {
  id: string;
  text: string;
  done: boolean;
  category?: string | null; // free label, e.g. "Obst & Gemüse" — only meaningful for shopping-style lists
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

export interface SavingsGoal {
  id: string;
  title: string;
  targetAmountCents: number;
  currentAmountCents: number;
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

export interface Todo {
  id: string;
  text: string;
  done: boolean;
  assignedToUid: string | null;
  dueDate?: string | null; // "YYYY-MM-DD", optional
  // Set when "Auch im Kalender anzeigen" is on — points at the matching
  // calendarEvents/{id} this todo also appears as. Editing text/dueDate here
  // or title/startDate there keeps both in sync; absent means todo-only.
  linkedEventId?: string | null;
  // Denormalized count of the todos/{id}/comments subcollection — kept in
  // sync by the onTodoCommentCreated Cloud Function (FieldValue.increment),
  // so the row can show "💬 3" without subscribing to every todo's thread.
  commentCount?: number;
  // Also set by onTodoCommentCreated — lets the TopBar activity feed query
  // "todos recently commented on" directly (orderBy lastCommentAt) without
  // a collection-group query across every couple's comments subcollections.
  lastCommentAt?: Timestamp;
  lastCommentByUid?: string;
  lastCommentByName?: string;
  lastCommentText?: string;
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

// A chronological discussion thread per todo — "erledigt, hab X besorgt",
// a question, whatever — both partners can post any number of times
// (unlike the Links feature's one-slot-per-person comment).
export interface TodoComment {
  id: string;
  text: string;
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
  // Set when the author edits it after the fact — only they can (see
  // firestore.rules); absent means never edited.
  editedAt?: Timestamp;
}

export interface ImportantDateCategory {
  id: string;
  name: string;
  color: string; // hex, e.g. "#6e2d49"
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

// "countdown" = a recurring-or-one-time date you count down TO (birthday,
// anniversary — "in 12 Tagen"); "countup" = an ongoing date you count up
// FROM ("Zusammen seit" — "seit 412 Tagen"), so repeatsYearly is meaningless
// for it. Absent on existing documents means "countdown", the original and
// only behavior before this field existed.
export type ImportantDateKind = "countdown" | "countup";

export interface ImportantDate {
  id: string;
  title: string;
  date: string; // "YYYY-MM-DD" — anchor/first occurrence for "countdown", start date for "countup"
  repeatsYearly: boolean;
  kind?: ImportantDateKind;
  categoryId?: string | null;
  // Chosen explicitly per entry so the Home screen isn't cluttered with
  // every important date — absent/false on existing documents, which is why
  // Home still falls back to showing the single nearest countdown when
  // nothing has been pinned yet (see Home.tsx).
  showOnHome?: boolean;
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

export interface Note {
  id: string;
  text: string;
  updatedByUid: string;
  updatedByName: string;
  updatedAt: Timestamp;
}

export interface LoveNote {
  text: string;
  fromUid: string;
  fromName: string;
  createdAt: Timestamp;
}

export interface Mood {
  uid: string;
  emoji: string;
  label: string;
  note: string;
  date: string; // "YYYY-MM-DD", so the UI can tell a "today" status from a stale one
  updatedAt: Timestamp;
}

// A named collection of links to compare (recipes, hotels, …) — mirrors
// WirList/ListItem's board + nested-subcollection shape.
export interface LinkBoard {
  id: string;
  name: string;
  icon: string; // emoji
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}

export interface SavedLink {
  id: string;
  url: string;
  title: string | null;
  description: string | null;
  image: string | null;
  siteName: string | null;
  // Fetching the og:* preview happens server-side after the link is saved —
  // "pending" while that's in flight, "error" if the site couldn't be read
  // (still shows the raw url so nothing is lost).
  previewStatus: "pending" | "ready" | "error";
  // Keyed by uid, same reasoning as comments below — each partner rates
  // independently (1–10, e.g. for comparing restaurants/recipes) and both
  // scores show side by side, instead of one overwriting the other's.
  ratings?: Record<string, { value: number; name: string }>;
  // Keyed by uid (not a single shared field) so each partner has their own
  // note — e.g. "Rezept mit weniger Salz gemacht" or "Restaurant war laut"
  // — without overwriting what the other wrote.
  comments?: Record<string, { text: string; name: string }>;
  createdByUid: string;
  createdByName: string;
  createdAt: Timestamp;
}
